// ============================================================
// Client-portal session (`yom_ps_…` token) — deliberately separate from
// the dashboard's auth slice: its own storage key, never the dashboard JWT,
// and a portal 401 only clears THIS session.
// ============================================================

import { createSlice, type PayloadAction } from '@reduxjs/toolkit'

import type { PortalSessionResponse } from '@/apis/portalTypes'

export const PORTAL_SESSION_KEY = 'yom_portal_session'

export interface PortalSessionState {
    token: string | null
    portalId: string | null
    user: { id: string; email: string; name: string } | null
}

const EMPTY: PortalSessionState = { token: null, portalId: null, user: null }

export function readPortalSession(): PortalSessionState {
    try {
        const raw = globalThis.localStorage?.getItem(PORTAL_SESSION_KEY)
        if (!raw) {
            return EMPTY
        }
        const s = JSON.parse(raw) as Partial<PortalSessionState>
        return typeof s.token === 'string' && s.token.startsWith('yom_ps_') && s.portalId
            ? { token: s.token, portalId: s.portalId, user: s.user ?? null }
            : EMPTY
    } catch {
        return EMPTY
    }
}

function write(s: PortalSessionState) {
    try {
        if (s.token) {
            globalThis.localStorage?.setItem(PORTAL_SESSION_KEY, JSON.stringify(s))
        } else {
            globalThis.localStorage?.removeItem(PORTAL_SESSION_KEY)
        }
    } catch {
        // storage unavailable — the session lives in memory for this tab
    }
}

const portalSessionSlice = createSlice({
    name: 'portalSession',
    initialState: readPortalSession,
    reducers: {
        portalSignedIn(_state, action: PayloadAction<PortalSessionResponse>) {
            const next: PortalSessionState = {
                token: action.payload.token,
                portalId: action.payload.portal.id,
                user: action.payload.user,
            }
            write(next)
            return next
        },
        portalSignedOut() {
            write(EMPTY)
            return EMPTY
        },
    },
})

export const { portalSignedIn, portalSignedOut } = portalSessionSlice.actions
export const portalSessionReducer = portalSessionSlice.reducer

/** The stored session counts only for the portal it was issued by. */
export function sessionForPortal(
    s: PortalSessionState,
    portalId: string | undefined,
): PortalSessionState | null {
    return s.token && portalId && s.portalId === portalId ? s : null
}
